import { Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import Logo from "@/components/Logo";

const links = [
  { to: "/#about", label: "About" },
  { to: "/#programme", label: "Programme" },
  { to: "/games", label: "Play & Learn" },
  { to: "/#gallery", label: "Gallery" },
  { to: "/#testimonials", label: "Parents" },
  { to: "/#contact", label: "Contact" },
];

export default function Navigation() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 60);
    }
  }, [location]);

  return (
    <header className="sticky top-0 z-50 w-full px-3 pt-3 md:px-6 md:pt-5" data-testid="site-navigation">
      <nav
        className={`mx-auto max-w-6xl flex items-center justify-between rounded-full border-[3px] border-brand-ink bg-white px-4 py-2 md:px-6 md:py-3 transition-all ${
          scrolled ? "shadow-brutal-sm" : "shadow-[3px_3px_0_0_rgba(43,45,66,1)]"
        }`}
      >
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
          <Logo size={52} />
          <span className="hidden sm:inline font-display text-xl md:text-2xl font-semibold leading-none whitespace-nowrap">
            <span className="text-brand-red">Cheeky</span>{" "}
            <span className="text-brand-ink">Monkeys</span>
          </span>
        </Link>

        <ul className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <li key={l.label}>
              <Link
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s|&/g, "-")}`}
                className="px-3 py-1.5 rounded-full text-sm font-bold text-brand-ink hover:bg-brand-yellow/60 transition-colors"
              >
                {l.label}
              </Link>
            </li>
          ))}
        </ul>

        <Link
          to="/#enroll"
          data-testid="nav-cta-enroll"
          className="hidden md:inline-flex items-center gap-2 rounded-full bg-brand-red text-white font-bold text-sm px-5 py-2 btn-brutal"
        >
          Enroll Now
        </Link>

        <button
          className="lg:hidden p-2 rounded-full border-2 border-brand-ink"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </nav>

      {open && (
        <div className="lg:hidden mx-auto max-w-6xl mt-2 rounded-3xl border-[3px] border-brand-ink bg-white p-4 shadow-brutal-sm" data-testid="nav-mobile-menu">
          <ul className="flex flex-col gap-1">
            {links.map((l) => (
              <li key={l.label}>
                <Link
                  to={l.to}
                  className="block px-4 py-3 rounded-2xl font-bold text-brand-ink hover:bg-brand-yellow/60"
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s|&/g, "-")}`}
                >
                  {l.label}
                </Link>
              </li>
            ))}
            <li>
              <Link
                to="/#enroll"
                className="block text-center mt-2 px-4 py-3 rounded-full bg-brand-red text-white font-bold btn-brutal"
                data-testid="nav-mobile-cta-enroll"
              >
                Enroll Now
              </Link>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
}
